import React, { useMemo } from 'react';
import { Tabs, message } from 'antd';
import { BarChartOutlined, CarOutlined, CalendarOutlined, EnvironmentOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import TrackedPerformanceOverviewTab from './TrackedPerformanceOverviewTab';
import TrackedTripPerformanceTab from './TrackedTripPerformanceTab';
import TrackedDailyOverviewTab from './TrackedDailyOverviewTab';
import TrackedStopPerformanceTab from './TrackedStopPerformanceTab';
import TrackedDriverPerformanceTab from './TrackedDriverPerformanceTab';

const { TabPane } = Tabs;

// View level constants
const VIEW_LEVELS = {
  TRIP_AGGREGATE: 1,   // Level 1: Aggregate trip performance
  TRIP_BY_DATE: 2,     // Level 2: Trip performance by date
  TRIP_DETAIL: 3,      // Level 3: Trip detail (stop-by-stop)
  STOP_DETAIL: 4       // Level 4: Stop detail (showing trips for a specific stop)
};

const TrackedTab = ({ 
  // State props
  loading,
  tripData,
  stopData, 
  driverData,
  filteredData,
  viewLevel,
  selectedTrip,
  selectedDate,
  selectedStop,
  selectedStopTypes, 
  dateRange,
  tripStartTimes,
  reportData,
  dateFilteredDataset,
  isDateDatasetLoaded,
  
  // Setter props
  setViewLevel,
  setSelectedTrip,
  setSelectedDate,
  setFilteredData,
  setSelectedStopTypes,
  
  // Function props
  handleTripClick,
  handleDateClick,
  handleBack,
  handleStopClick,
  filterDataByStopType 
}) => {
  
  // Helper to check if a stop is tracked (has actual timing data)
  const isStopTracked = (stop) => {
    const hasActualDeparture = Number.isFinite(stop.ACTUAL_DEPARTURE_TIME);
    const hasActualArrival = Number.isFinite(stop.ACTUAL_ARRIVAL_TIME);
    return hasActualDeparture || hasActualArrival;
  };

  // Helper to calculate tracking statistics for a group of stops
  const calculateTrackingStats = (stops) => {
    if (!stops || stops.length === 0) {
      return {
        trackedPercent: 0,
        untrackedPercent: 0,
        trackedCount: 0,
        untrackedCount: 0,
        totalStops: 0
      };
    }
    
    const trackedCount = stops.filter(stop => isStopTracked(stop)).length;
    const untrackedCount = stops.length - trackedCount;
    
    return {
      trackedPercent: (trackedCount / stops.length) * 100,
      untrackedPercent: (untrackedCount / stops.length) * 100,
      trackedCount,
      untrackedCount,
      totalStops: stops.length
    };
  };

  // Get trip data by date for tracking analysis
  const getTripDateDataForTracking = async (tripKey, filteredData = null) => {
    let tripRecords = [];
    
    // Helper to get trip key
    const getTripKey = (record) => record.KEY || record.TRIP_ID;
    
    // Use filtered data if provided, otherwise use date-filtered dataset
    if (filteredData && Array.isArray(filteredData)) {
      console.log(`Using provided filtered data with ${filteredData.length} records for trip ${tripKey}`);
      // Filter for just the current trip from the provided filtered data
      tripRecords = filteredData.filter(r => getTripKey(r) === tripKey);
    } else if (isDateDatasetLoaded && dateFilteredDataset.length > 0) {
      console.log(`Using date-filtered dataset with ${dateFilteredDataset.length} records for trip ${tripKey}`);
      // Filter for selected trip from the date-filtered dataset
      tripRecords = dateFilteredDataset.filter(r => getTripKey(r) === tripKey);
    } else {
      console.log(`No filtered data available for tracking analysis of trip ${tripKey}`);
      // Filter from stopData as fallback
      tripRecords = stopData.filter(r => getTripKey(r) === tripKey);
    }
    
    console.log(`Found ${tripRecords.length} records for trip ${tripKey}`);
    
    // Debug: Check the date information in the records
    const uniqueDates = [...new Set(tripRecords.map(r => r.DATE_ID))];
    console.log(`Found ${uniqueDates.length} unique dates for trip ${tripKey}:`, uniqueDates);

    // Group by date - ensure we're treating DATE_ID consistently as strings
    const dateGroups = {};
    tripRecords.forEach(record => {
      if (!record.DATE_ID) return;
      
      // Convert DATE_ID to string to ensure consistent grouping
      const dateId = String(record.DATE_ID);
      
      if (!dateGroups[dateId]) {
        dateGroups[dateId] = {
          tripKey: tripKey,
          tripId: record.TRIP_ID, // Keep original TRIP_ID for display
          dateId: dateId,
          route: record.ROUTE,
          date: record.DATE_FORMATTED || dayjs(dateId).format('YYYY-MM-DD'),
          stops: [],
          firstStops: [],
          midStops: [],
          lastStops: []
        };
      }
      
      // Add to appropriate stop category
      if (record.TSN_FIRST_LAST_IND === 'F') {
        dateGroups[dateId].firstStops.push(record);
      } else if (!record.TSN_FIRST_LAST_IND?.length || record.TSN_FIRST_LAST_IND === 'M') {
        dateGroups[dateId].midStops.push(record);
      } else if (record.TSN_FIRST_LAST_IND === 'L') {
        dateGroups[dateId].lastStops.push(record);
      }
      
      // Add to all stops
      dateGroups[dateId].stops.push(record);
    });
    
    console.log(`Organised data into ${Object.keys(dateGroups).length} date groups:`, Object.keys(dateGroups));
    
    // Calculate tracking stats for each date and convert to array
    return Object.values(dateGroups).map(dateGroup => {
      const firstStopStats = calculateTrackingStats(dateGroup.firstStops);
      const midStopStats = calculateTrackingStats(dateGroup.midStops);
      const lastStopStats = calculateTrackingStats(dateGroup.lastStops);
      const overallStats = calculateTrackingStats(dateGroup.stops);
      
      return {
        tripKey: dateGroup.tripKey,
        tripId: dateGroup.tripId, // Keep original TRIP_ID for display
        dateId: dateGroup.dateId,
        date: dateGroup.date,
        route: dateGroup.route,
        stopCount: dateGroup.stops.length,
        firstStopStats,
        midStopStats,
        lastStopStats,
        overallStats
      };
    });
  };

  // Handle trip click for tracking analysis
  const handleTripClickForTracking = async (tripKey) => {
    // Find the selected trip
    const trip = trackedTripData.find(t => t.tripKey === tripKey);
    if (!trip) {
      message.error("Trip not found");
      return;
    }

    // Get date-level data for this trip using filtered data
    const dateData = await getTripDateDataForTracking(tripKey, stopData);
    setSelectedTrip(trip);
    setViewLevel(VIEW_LEVELS.TRIP_BY_DATE);
    setFilteredData(dateData);
  };

  // Process trip data for tracking analysis
  const processTripsDataForTracking = (data) => {
    if (!data || data.length === 0) return [];
    
    // Group by trip
    const tripGroups = {};
    
    data.forEach(stop => {
      const getTripKey = (record) => record.KEY || record.TRIP_ID;
      const tripKey = getTripKey(stop);
      if (!tripGroups[tripKey]) {
        tripGroups[tripKey] = {
          tripKey,
          tripId: stop.TRIP_ID, // Keep original TRIP_ID for display
          route: stop.ROUTE,
          stops: [],
          firstStops: [],
          midStops: [],
          lastStops: [],
          dates: new Set(),
          driverId: stop.DRIVER_ID,
          depot: stop.DEPOT,
          operatorId: stop.OPERATOR_ID,
          contract: stop.CONTRACT
        };
      }
      
      tripGroups[tripKey].stops.push(stop);
      
      // Categorize by stop type
      if (stop.TSN_FIRST_LAST_IND === 'F') {
        tripGroups[tripKey].firstStops.push(stop);
      } else if (stop.TSN_FIRST_LAST_IND === 'M') {
        tripGroups[tripKey].midStops.push(stop);
      } else if (stop.TSN_FIRST_LAST_IND === 'L') {
        tripGroups[tripKey].lastStops.push(stop);
      }
      
      if (stop.DATE_ID) {
        tripGroups[tripKey].dates.add(stop.DATE_ID);
      }
    });
    
    // Calculate tracking stats for each trip and convert to array
    const result = Object.values(tripGroups).map(trip => {
      // Calculate tracking metrics
      const firstStopStats = calculateTrackingStats(trip.firstStops);
      const midStopStats = calculateTrackingStats(trip.midStops);
      const lastStopStats = calculateTrackingStats(trip.lastStops);
      const overallStats = calculateTrackingStats(trip.stops);
      
      return {
        tripKey: trip.tripKey,
        tripId: trip.tripId, // Keep original TRIP_ID for display
        route: trip.route,
        dateCount: trip.dates.size,
        dates: Array.from(trip.dates),
        stopCount: trip.stops.length,
        firstStopStats,
        midStopStats,
        lastStopStats,
        overallStats,
        driverId: trip.driverId,
        depot: trip.depot,
        operatorId: trip.operatorId,
        contract: trip.contract,
        recordCount: trip.stops.length
      };
    });
    
    return result;
  };

  // Memoize the processed tracking trip data
  const trackedTripData = useMemo(() => {
    return processTripsDataForTracking(stopData);
  }, [stopData]);

  return (
    <Tabs defaultActiveKey="overview" type="card">
      <TabPane 
        tab={
          <span>
            <BarChartOutlined />
            Performance Overview
          </span>
        } 
        key="overview"
      >
        <TrackedPerformanceOverviewTab
          stopData={stopData}
          selectedStopTypes={selectedStopTypes}
          setSelectedStopTypes={setSelectedStopTypes}
          dateRange={dateRange}
          tripData={tripData}
          filterDataByStopType={filterDataByStopType}
        />
      </TabPane>
      
      <TabPane 
        tab={
          <span>
            <CarOutlined />
            Trip Performance
          </span>
        } 
        key="tripPerformance"
      >
        <TrackedTripPerformanceTab
          tripData={trackedTripData}
          stopData={stopData}
          filteredData={viewLevel === VIEW_LEVELS.TRIP_AGGREGATE ? trackedTripData : filteredData}
          viewLevel={viewLevel}
          selectedTrip={selectedTrip}
          selectedDate={selectedDate}
          selectedStop={selectedStop}
          tripStartTimes={tripStartTimes}
          onTripClick={handleTripClickForTracking}
          onDateClick={handleDateClick}
          onBack={handleBack}
          reportData={reportData}
          dateFilteredDataset={dateFilteredDataset}
          isDateDatasetLoaded={isDateDatasetLoaded}
        />
      </TabPane>
      
      <TabPane 
        tab={
          <span>
            <CalendarOutlined />
            Daily Overview
          </span>
        } 
        key="dailyOverview"
      >
        <TrackedDailyOverviewTab
          data={stopData}
          selectedStopTypes={selectedStopTypes}
          onStopTypeChange={setSelectedStopTypes}
        />
      </TabPane>
      
      <TabPane 
        tab={
          <span>
            <EnvironmentOutlined />
            Stop Performance
          </span>
        } 
        key="stopPerformance"
      >
        <TrackedStopPerformanceTab
          stopData={stopData}
          onStopClick={handleStopClick}
          tripStartTimes={tripStartTimes}
        />
      </TabPane>
      
      <TabPane 
        tab={
          <span>
            <UserOutlined />
            Driver Performance
          </span>
        } 
        key="driverPerformance"
      >
        <TrackedDriverPerformanceTab
          driverData={driverData}
          stopData={stopData}
          tripData={tripData}
          tripStartTimes={tripStartTimes}
        />
      </TabPane>
    </Tabs>
  );
};

export default TrackedTab;